import type { ISO639_1_Language, PlagiarismResponse } from "@shared/types";
import { post } from "./api";
import { trackEvent } from "./metrics";

export interface PlagiarismRequestData {
	text: string;
	website?: string;
	excluded_sources?: string[];
	language?: ISO639_1_Language;
	country?: string;
}

//Winston supports only 2 letter format languages
const convertMap: Partial<Record<ISO639_1_Language, string>> = {
	es_419: "es",
	zh_cn: "zh-cn",
	zh_tw: "zh-tw",
	pt_br: "pt",
	pt_pt: "pt",
	fil: "tl",
};

const getLanguage = (value: ISO639_1_Language) => {
	if (convertMap[value]) {
		return convertMap[value];
	}
	return value;
};

export function plagiarism(
	data: PlagiarismRequestData,
): Promise<PlagiarismResponse> {
	const normalizedData = {
		...data,
		...(data.language && { language: getLanguage(data.language) }),
	};
	const t0 = performance.now();

	return post("plagiarism", normalizedData)
		.then((response) => {
			const t1 = performance.now();

			trackEvent({
				event: "plagiarism_responded",
				metadata: {
					time: t1 - t0,
					textLength: data.text.length,
				},
			});

			return response;
		})
		.catch((error) => {
			// console.error("Error in plagiarism service:", error);
			throw new ClientPlagiarismError(
				"An error occurred while detecting plagiarism",
				{ cause: error },
			);
		});
}

export const PLAGIARISM_SCORE_THRESHOLDS = {
	veryLikelyPlagiarism: 0.85,
	mostlyPlagiarism: 0.65,
	mixed: 0.45,
	mostlyOriginal: 0.25,
	veryLikelyOriginal: 0,
};

class ClientPlagiarismError extends Error {
	cause: unknown;
	constructor(message: string, cause?: unknown) {
		super(message);
		this.name = "ClientPlagiarismError";
		this.cause = cause;
	}
}
